/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-plusplus */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  usePatch,
  getGridTargetTitleDetails,
  getLocationTargetTitleDetails,
  getLocationTargetErrorMessage,
  globalErrorMsg,
} from '@bingads-webui-campaign/hotel-common-upsert';
import { ErrorString, locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import { Collapse, ToggleTitle } from '@bingads-webui-campaign/wizard-jumbo-card';
import { Link, Stack, DefaultButton } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import {
  DeviceTargetSetting,
  LocationTargetSetting,
} from '@bingads-webui-campaign/hotel-common-targets';
import { defaultCampaignBasicData } from './default-value';

const getStyles = () => ({
  CampaignTargets: {
    marginTop: '40px',
  },
  title: {
    fontWeight: 600,
    fontSize: '20px',
  },
  subtitle: {
    marginBottom: '16px',
    fontSize: '20px',
  },
  mt33: {
    marginTop: '33px',
  },
});


export const CampaignTargets = (props) => {
  const {
    campaign,
    patchCampaign,
    errMsg,
    patchErrMsg,
    targetsApiErrors,
    patchTargetsApiErrMsg,
  } = props;
  const classes = useDefaultStyles(getStyles);

  const i18n = useContext(I18nContext);

  const allPanelItems = ['location', 'device'];
  const [activeKeys, setActiveKeys] = useState(['location', 'device']);
  useEffect(() => {
    const hasApiErrorKeys = Object.keys(targetsApiErrors);
    const needToExpandKeys = [];
    allPanelItems.forEach((key) => {
      if (hasApiErrorKeys.includes(key) && targetsApiErrors[key] && targetsApiErrors[key].length) {
        patchErrMsg(key, globalErrorMsg);
        needToExpandKeys.push(key);
      }
    });
    setActiveKeys(activeKeys.concat(hasApiErrorKeys));
  }, [targetsApiErrors]);
  const onCollapseAll = () => {
    if (activeKeys.length) {
      setActiveKeys([]);
    } else {
      setActiveKeys(allPanelItems);
    }
  };

  const setLocation = usePatch(patchCampaign, 'location');
  const setDevice = usePatch(patchCampaign, 'device');

  const {
    location, device, bundleCountries, bundleLocationCriterions,
  } = campaign;

  const {
    device: { currentLevelSettings: deviceDefaultCurrentLevelSettings },
  } = defaultCampaignBasicData;

  const resetTarget = (setFn, targetData, targetDefaultCurrentLevelSettings, targetName) => {
    let tempCurrentLevelSettings = [...targetData.currentLevelSettings];
    for (let i = 0; i < tempCurrentLevelSettings.length; i++) {
      if (tempCurrentLevelSettings[i]) {
        const tempId = tempCurrentLevelSettings[i].Id;
        const tempObj = { ...targetDefaultCurrentLevelSettings[i] };
        if (tempCurrentLevelSettings[i].Id) {
          tempCurrentLevelSettings[i] = tempObj;
          tempCurrentLevelSettings[i].Id = tempId;
          tempCurrentLevelSettings[i].Status = 'Deleted';
        } else {
          tempCurrentLevelSettings.splice(i, 1);
          i--;
        }
      }
    }
    tempCurrentLevelSettings = targetDefaultCurrentLevelSettings.concat(tempCurrentLevelSettings);
    setFn({ ...targetData, currentLevelSettings: tempCurrentLevelSettings, reset: true });
    patchTargetsApiErrMsg(targetName, []);
  };

  // Turn off --- you set nothing, you use default settings 0 0 0
  // Turn on --- you override 0 0 0
  const onChangeToggle = (fn, key, value) => {
    fn({ ...campaign[key], useParentLevel: !value });
  };

  const campaignBidStrategyType = campaign && campaign.bidStrategy && campaign.bidStrategy.option;
  const locationTitle = (<ToggleTitle
    titleKey={_TL_('Location')}
    topicKey="pop_BA_Settings_LocationTargeting"
    details={getLocationTargetTitleDetails(i18n, location, false, campaignBidStrategyType)}
    isChecked={!location.useParentLevel}
    handleChangeToggle={(_e, value) => {
      if (location.useParentLevel === true &&
        location.bidMode === true &&
        location.currentLevelSettings.targetOption === locationTargetsOptionKeys.everywhere) {
        setLocation({
          ...location,
          useParentLevel: false,
          bidMode: false,
          errors: undefined,
        });
      } else {
        setLocation({
          ...location,
          useParentLevel: !value,
          errors: undefined,
        });
      }
    }}
  />);

  const deviceTitle = (<ToggleTitle
    titleKey={_TL_('Device')}
    topicKey="pop_BA_CampaignCreate_AudTargets_Device"
    details={device.useParentLevel ? '' : getGridTargetTitleDetails(i18n, device)}
    isChecked={!device.useParentLevel}
    handleChangeToggle={(_e, value) => { onChangeToggle(setDevice, 'device', value); }}
  />);

  return (
    <div className={classnames('campaign-targets-section', classes.CampaignTargets)}>
      <div>
        <div className={classes.title}>
          {i18n.getString(_TL_('Campaign targets'))}
        </div>
        <Stack horizontal horizontalAlign="space-between" tokens={{ padding: '0 0 12px 0' }}>
          <span className={classes.description}> {i18n.getString(_TL_('Who do you want to target for this campaign? You can select any combination of the criteria below.'))}</span>
          <Link className="jumbo-card-control-all-btn" onClick={onCollapseAll}>{activeKeys.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
        </Stack>
      </div>

      <Collapse activeKeys={activeKeys} setActiveKeys={setActiveKeys}>
        <Collapse.Panel
          key="location"
          title={locationTitle}
          error={getLocationTargetErrorMessage(location, campaignBidStrategyType)}
        >
          <LocationTargetSetting
            data={location}
            setData={setLocation}
            bundleCountries={bundleCountries}
            bundleLocationCriterions={bundleLocationCriterions}
            scope={props.scope}
            bidStrategyType={campaignBidStrategyType}
          />
        </Collapse.Panel>
        <Collapse.Panel key="device" title={deviceTitle} error={errMsg.device}>
          <DeviceTargetSetting
            data={device}
            setData={setDevice}
            patchErrMsg={patchErrMsg}
            patchTargetsApiErrMsg={patchTargetsApiErrMsg}
            apiErrors={targetsApiErrors.device}
            bidStrategyType={campaignBidStrategyType}
          />
          {!device.useParentLevel && targetsApiErrors.device && targetsApiErrors.device.map(item => (<ErrorString error={item} />))}
          <Stack horizontal horizontalAlign="end" className={classes.mt33}>
            <DefaultButton text={i18n.getString(_TL_('Reset'))} disabled={device.useParentLevel} onClick={() => { resetTarget(setDevice, device, deviceDefaultCurrentLevelSettings, 'device'); }} />
          </Stack>
        </Collapse.Panel>
      </Collapse>
    </div>);
};

CampaignTargets.propTypes = {
  campaign: PropTypes.shape({}),
  errMsg: PropTypes.shape({}),
  patchCampaign: PropTypes.func.isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
      campaignId: PropTypes.number,
    }).isRequired,
  }).isRequired,
  patchErrMsg: PropTypes.func.isRequired,
  targetsApiErrors: PropTypes.shape({}),
  patchTargetsApiErrMsg: PropTypes.func,
};

CampaignTargets.defaultProps = {
  campaign: {},
  errMsg: {},
  targetsApiErrors: {},
  patchTargetsApiErrMsg() {},
};
